/**
 * Λογότυπο απόδειξης: δεν υπάρχει endpoint ανεβάσματος εικόνας — όπως ο παλιός επεξεργαστής προτύπων,
 * η εικόνα σμικρύνεται στον browser (≤512 px, πάνω σε λευκό ώστε τα διάφανα να μη βγαίνουν μαύρα) και
 * αποθηκεύεται ως data-URI PNG στο receipt_logo_url (PUT /printing/receipt-look). Ο agent τη ζωγραφίζει
 * σε «Εικόνα»/«Υβριδική» και τη σμικρύνει μόνος του στο ύψος του χαρτιού.
 */

export const LOGO_MAX_BYTES = 3 * 1024 * 1024;
const LOGO_MAX_PX = 512;
const TYPES = /^image\/(png|jpe?g|webp)$/;

export type LogoErrorCode = 'type' | 'size' | 'read';

export class LogoError extends Error {
  code: LogoErrorCode;

  constructor(code: LogoErrorCode) {
    super(code);
    this.code = code;
  }
}

/** Έλεγχος πριν από την ανάγνωση (τύπος, μέγεθος). */
export function checkLogoFile(file: File): LogoErrorCode | null {
  if (!TYPES.test(file.type)) return 'type';
  if (file.size > LOGO_MAX_BYTES) return 'size';
  return null;
}

export function prepareLogo(file: File): Promise<string> {
  const bad = checkLogoFile(file);
  if (bad) return Promise.reject(new LogoError(bad));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new LogoError('read'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new LogoError('type'));
      img.onload = () => {
        let { width, height } = img;
        if (!width || !height) { reject(new LogoError('type')); return; }
        if (width > LOGO_MAX_PX || height > LOGO_MAX_PX) {
          const s = Math.min(LOGO_MAX_PX / width, LOGO_MAX_PX / height);
          width = Math.round(width * s);
          height = Math.round(height * s);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new LogoError('read')); return; }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}
